import React, { useEffect, useState } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from './ui/breadcrumb';
import { GlobalSearch } from './GlobalSearch';
import { QuickAddLead } from './QuickAddLead';
import { EntitySummaryPanel } from './EntitySummaryPanel';
import { NotificationBell } from './NotificationBell';
import { emitLeadChanged } from '../lib/events';
import { ISTClock } from './ISTClock';
import { appForPath, currentNavLabel, itemAllowed } from '../lib/apps';
import {
  LayoutGrid, Table2, LogOut, Megaphone, Plus, Menu, Grip, CalendarDays, Home,
} from 'lucide-react';

const NavItem = ({ to, icon: Icon, label, testId, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        isActive
          ? 'bg-[hsl(var(--blue-soft))] text-primary ring-1 ring-[hsl(var(--primary)/0.18)]'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`
    }
  >
    <Icon size={18} />
    <span>{label}</span>
  </NavLink>
);

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/** App-specific sidebar nav built from the registry (perm-gated + coming-soon). */
const WorkspaceNav = ({ app, hasPerm, user, auth }) => (
  <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto" data-testid="workspace-sidebar">
    {(app.nav || []).map((group) => (
      <div key={group.group} className="pb-2">
        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          {group.group}
        </div>
        <div className="space-y-1">
          {group.items.map((item) => {
            if (item.soon) {
              return (
                <div key={item.label} aria-disabled="true"
                  className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-muted-foreground/60 cursor-not-allowed select-none"
                  data-testid={`workspace-nav-soon-${slugify(item.label)}`}>
                  <span>{item.label}</span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground">Soon</Badge>
                </div>
              );
            }
            if (!itemAllowed(item, hasPerm, user, auth)) return null;
            return (
              <NavItem key={`${item.label}-${item.path}`} to={item.path} icon={item.icon} label={item.label} end={item.end}
                testId={`workspace-nav-${slugify(item.label)}`} />
            );
          })}
        </div>
      </div>
    ))}
    {!app.live && (
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        Planned Modules
      </div>
    )}
    {!app.live && (app.planned || []).map((p) => (
      <div key={p} aria-disabled="true"
        className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-muted-foreground/60 cursor-not-allowed select-none"
        data-testid={`workspace-nav-soon-${slugify(p)}`}>
        <span>{p}</span>
        <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground">Soon</Badge>
      </div>
    ))}
  </nav>
);

const SidebarHeader = ({ app }) => (
  <div className="px-3 pt-3">
    <Link to="/" data-testid="workspace-back-to-launcher"
      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
      <Grip size={16} />
      <span>All Apps</span>
    </Link>
    <div className="flex items-center gap-2.5 px-3 py-3 border-b">
      <div className="h-8 w-8 rounded-lg bg-[hsl(var(--blue-soft))] flex items-center justify-center">
        <app.icon size={17} className="text-primary" />
      </div>
      <div className="min-w-0">
        <div className="font-heading font-semibold text-sm leading-tight truncate" data-testid="workspace-app-name">{app.name}</div>
        {!app.live && <Badge variant="outline" className="text-[9px] px-1 py-0 mt-0.5 text-muted-foreground">Coming Soon</Badge>}
      </div>
    </div>
  </div>
);

const SidebarFooter = ({ user, accessRole, onLogout }) => (
  <div className="border-t px-4 py-3">
    <div className="flex items-center justify-between">
      <div className="min-w-0">
        <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="text-xs text-muted-foreground truncate" data-testid="sidebar-user-role">{accessRole}</div>
      </div>
      <Button variant="ghost" size="icon" onClick={onLogout} data-testid="logout-button" aria-label="Logout">
        <LogOut size={16} />
      </Button>
    </div>
  </div>
);

const DesktopSidebar = ({ app, user, accessRole, hasPerm, auth, onLogout }) => (
  <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col border-r bg-card z-30">
    <SidebarHeader app={app} />
    <WorkspaceNav app={app} hasPerm={hasPerm} user={user} auth={auth} />
    <SidebarFooter user={user} accessRole={accessRole} onLogout={onLogout} />
  </aside>
);

const WorkspaceBreadcrumbs = ({ app, pathname }) => {
  const pageLabel = currentNavLabel(app, pathname);
  return (
    <Breadcrumb className="mb-3" data-testid="workspace-breadcrumbs">
      <BreadcrumbList className="text-xs">
        <BreadcrumbItem>
          <BreadcrumbLink asChild><Link to="/" className="inline-flex items-center gap-1"><Home size={11} /> Home</Link></BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        {pageLabel ? (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to={app.path}>{app.name}</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{pageLabel}</BreadcrumbPage></BreadcrumbItem>
          </>
        ) : (
          <BreadcrumbItem><BreadcrumbPage>{app.name}</BreadcrumbPage></BreadcrumbItem>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

const DesktopTopBar = ({ onQuickAdd, inWorkspace }) => (
  <header className={`hidden lg:flex fixed top-0 ${inWorkspace ? 'left-60' : 'left-0'} right-0 z-20 items-center gap-3 h-14 px-6 border-b bg-card/95 backdrop-blur`}>
    {!inWorkspace && (
      <Link to="/" className="flex items-center gap-2 mr-2" data-testid="topnav-home-logo">
        <div className="h-8 w-8 rounded-lg bg-white border border-border/60 flex items-center justify-center">
          <img src="/logo.png" alt="Futurists logo" className="h-5 w-5 object-contain" />
        </div>
        <span className="font-heading font-bold text-sm">Futurists ERP</span>
      </Link>
    )}
    <GlobalSearch variant="bar" />
    <div className="flex-1" />
    <ISTClock />
    <NotificationBell />
    <Button size="sm" onClick={onQuickAdd} data-testid="topnav-quick-add-button">
      <Plus size={15} className="mr-1" /> Quick Add Lead
    </Button>
  </header>
);

const MobileTopBar = ({ app, user, accessRole, hasPerm, auth, onQuickAdd, onLogout }) => (
  <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-3 h-14 border-b bg-card/95 backdrop-blur">
    <div className="flex items-center gap-1.5">
      {app ? (
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="workspace-sidebar-open-button" aria-label="Open app menu">
              <Menu size={18} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0 flex flex-col" data-testid="workspace-sidebar-sheet">
            <SidebarHeader app={app} />
            <WorkspaceNav app={app} hasPerm={hasPerm} user={user} auth={auth} />
            <SidebarFooter user={user} accessRole={accessRole} onLogout={onLogout} />
          </SheetContent>
        </Sheet>
      ) : (
        <div className="h-8 w-8 rounded-lg bg-white border border-border/60 flex items-center justify-center">
          <img src="/logo.png" alt="Futurists logo" className="h-5 w-5 object-contain" data-testid="mobile-logo" />
        </div>
      )}
      <ISTClock compact />
    </div>
    <div className="flex items-center gap-0.5">
      <GlobalSearch variant="icon" />
      <NotificationBell />
      <Button variant="ghost" size="icon" onClick={onQuickAdd} data-testid="mobile-quick-add-button" aria-label="Quick add lead">
        <Plus size={18} />
      </Button>
      <Button variant="ghost" size="icon" onClick={onLogout} data-testid="mobile-logout-button" aria-label="Logout">
        <LogOut size={16} />
      </Button>
    </div>
  </header>
);

const MobileNavItem = ({ to, icon: Icon, label, testId, end }) => (
  <NavLink to={to} end={end} data-testid={testId}
    className={({ isActive }) =>
      `flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 text-[10px] font-medium ${
        isActive ? 'text-primary' : 'text-muted-foreground'
      }`
    }>
    <Icon size={20} />
    <span>{label}</span>
  </NavLink>
);

const MobileBottomNav = ({ hasPerm }) => (
  <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 flex items-stretch border-t bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
    <MobileNavItem to="/" end icon={Grip} label="Apps" testId="bottom-nav-home-link" />
    <MobileNavItem to="/sales" end icon={LayoutGrid} label="Board" testId="bottom-nav-kanban-link" />
    <MobileNavItem to="/sales/leads" icon={Table2} label="Leads" testId="bottom-nav-leads-link" />
    {hasPerm('meetings', 'view') && <MobileNavItem to="/sales/meetings" icon={CalendarDays} label="Meetings" testId="bottom-nav-meetings-link" />}
    <MobileNavItem to="/announcements" icon={Megaphone} label="News" testId="bottom-nav-announcements-link" />
  </nav>
);

export const AppShell = ({ children }) => {
  const auth = useAuth();
  const { user, logout, hasPerm, accessRole } = auth;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [members, setMembers] = useState([]);

  const app = pathname === '/' ? null : appForPath(pathname);
  const inWorkspace = Boolean(app);

  useEffect(() => {
    if (!quickAddOpen || members.length) return;
    api.get('/team-members').then(({ data }) => setMembers(data)).catch(() => {});
  }, [quickAddOpen, members.length]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const onGlobalLeadCreated = (lead) => {
    window.dispatchEvent(new CustomEvent('lead:created', { detail: lead }));
    emitLeadChanged(lead);
  };

  return (
    <div className="min-h-screen bg-background">
      {inWorkspace && (
        <DesktopSidebar app={app} user={user} accessRole={accessRole} hasPerm={hasPerm} auth={auth} onLogout={handleLogout} />
      )}
      <DesktopTopBar onQuickAdd={() => setQuickAddOpen(true)} inWorkspace={inWorkspace} />
      <MobileTopBar app={app} user={user} accessRole={accessRole} hasPerm={hasPerm} auth={auth}
        onQuickAdd={() => setQuickAddOpen(true)} onLogout={handleLogout} />

      <main className={`${inWorkspace ? 'lg:pl-60' : ''} lg:pt-14 pb-20 lg:pb-6`}>
        <div className="px-3 sm:px-4 lg:px-6 py-4">
          {inWorkspace && <WorkspaceBreadcrumbs app={app} pathname={pathname} />}
          {children}
        </div>
      </main>

      <MobileBottomNav hasPerm={hasPerm} />

      <QuickAddLead open={quickAddOpen} onClose={() => setQuickAddOpen(false)} members={members} onCreated={onGlobalLeadCreated} />
      <EntitySummaryPanel />
    </div>
  );
};
